import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { EMBEDDED, inAppBrowser, iosNotSafari, isIOS, useDevice } from '@/lib/device';
import { HomeScreenSteps, OpenInBrowser } from '@/components/HomeScreenSteps';

/** Sentinel's own reset page for this country (as apps/portal lib/countries.ts), where the new password is set. */
const RESET_URL = `https://${String(import.meta.env.VITE_COUNTRY_CODE ?? 'za').toLowerCase()}.app.cleanmybed.com/reset-password`;

// Messages copied from the portal's LoginPage so people see the same wording in both apps.
function friendlyError(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('load failed')) {
    return 'Unable to connect. Please check your internet connection or try mobile data.';
  }
  if (lower.includes('invalid login') || lower.includes('invalid credentials')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Your email has not been confirmed. Check your inbox for a confirmation link.';
  }
  if (lower.includes('too many requests') || lower.includes('rate limit')) {
    return 'Too many login attempts. Please wait a moment and try again.';
  }
  return msg || 'Failed to sign in. Please try again.';
}

export function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetNote, setResetNote] = useState<string | null>(null);
  // One reset at a time, and none again once sent to this address: a second tap used to hit the rate limit and
  // overwrite 'sent' with 'did not send'. The note shows under the button tapped (mobile review, 19 Oct).
  const [resetState, setResetState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [resetFrom, setResetFrom] = useState<'error' | 'foot'>('foot');
  const noteRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (resetNote) noteRef.current?.scrollIntoView({ block: 'nearest' }); }, [resetNote]);
  const { standalone } = useDevice();
  // iPhone: the home-screen app does not keep a Safari sign-in, so the home screen comes first (Gary, 19 Oct).
  const homeFirst = isIOS && !standalone && !EMBEDDED && !inAppBrowser && !iosNotSafari;
  const [signInHere, setSignInHere] = useState(false);

  async function sendReset(from: 'error' | 'foot') {
    if (resetState !== 'idle') return;
    setResetFrom(from);
    const to = email.trim();
    if (!to) { setResetNote('Type your email above first.'); return; }
    setResetState('sending');
    setResetNote(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(to, { redirectTo: RESET_URL });
    if (!resetError) {
      setResetState('sent');
      setResetNote(`Reset link sent to ${to}. Set a new password there, then come back here and sign in.`);
    } else if (resetError.status === 429 || resetError.code === 'over_email_send_rate_limit') {
      setResetState('sent');
      setResetNote(`A reset link was already sent to ${to}. Check your inbox (and spam).`);
    } else {
      setResetState('idle');
      setResetNote('That did not send. Check the email and try again.');
    }
  }

  const resetLabel = (idle: string) => (resetState === 'sending' ? 'Sending...' : resetState === 'sent' ? 'Reset link sent' : idle);
  const note = resetNote && <p ref={noteRef} className="chats-login-foot" role="status">{resetNote}</p>;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResetNote(null);
    setBusy(true);
    const message = await signIn(email.trim(), password);
    if (message) setError(friendlyError(message));
    setBusy(false);
  }

  return (
    <div className="chats-login">
      <div className="chats-login-hero">
        <img className="chats-login-icon" src={`${import.meta.env.BASE_URL}icon-192.png`} alt="" width={72} height={72} />
        <h1 className="chats-login-title">CMB Chats</h1>
        <p className="chats-login-sub">Your CleanMyBed customer chats</p>
      </div>
      {(inAppBrowser || iosNotSafari) && <div className="chats-login-form"><OpenInBrowser /></div>}
      {homeFirst && !signInHere ? (
        <div className="chats-login-form">
          <p className="chats-login-hint"><b>First, add CMB Chats to your home screen.</b> Then open it from there and sign in once. A sign-in here in Safari does not carry over.</p>
          <HomeScreenSteps />
          <button type="button" className="chats-link" onClick={() => setSignInHere(true)}>Sign in here instead</button>
        </div>
      ) : (
      <form className="chats-login-form" onSubmit={handleSubmit}>
        <p className="chats-login-hint">Sign in with your Sentinel email and password.</p>
        {error && <p className="chats-login-error" role="alert">{error}</p>}
        {error?.startsWith('Incorrect email or password') && (
          <button type="button" className="chats-btn chats-btn--ghost" disabled={resetState !== 'idle'} onClick={() => void sendReset('error')}>{resetLabel('Email me a reset link')}</button>
        )}
        {resetFrom === 'error' && error && note}
        <label className="chats-field-label" htmlFor="email">Email</label>
        <input
          id="email"
          className="chats-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setResetNote(null); setResetState('idle'); }}
          disabled={busy}
          required
        />
        <label className="chats-field-label" htmlFor="password">Password</label>
        <input
          id="password"
          className="chats-input"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          required
        />
        <button type="button" className="chats-link" onClick={() => setShowPassword((v) => !v)}>{showPassword ? 'Hide password' : 'Show password'}</button>
        <button className="chats-btn" type="submit" disabled={busy}>
          {busy ? 'Signing in...' : 'Sign in'}
        </button>
        <button type="button" className="chats-link" disabled={resetState !== 'idle'} onClick={() => void sendReset('foot')}>{resetLabel('Forgot password?')}</button>
        {!(resetFrom === 'error' && error) && note}
      </form>
      )}
    </div>
  );
}
